/**
 * Admin → Promotions.
 *
 * Two kinds of platform discount live here (owner, Jun 2026):
 *  - Coaching campaigns: one campaign, one shared redemption pool, and a code per
 *    practitioner so redemptions can be tracked by who brought them in. Tracking
 *    only — nobody is paid for a redemption. Paid-in-full programs only.
 *  - Livestream promotions: per stream, either a sale price shown with no code or
 *    a code for pre-sale marketing, covering the live seat and the archived replay.
 *
 * Workshop discounts are not here — practitioners run those from their own hub.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Ticket, Trash2, RefreshCw, Users, CalendarClock, Tv, Plus, BarChart3, Check, AlertTriangle,
  Pencil, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIERS = ['silver', 'gold', 'platinum'];

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// FastAPI returns a 422 `detail` as an ARRAY of objects. Handing that straight to a
// toast makes React throw and drops the whole admin page into the error boundary.
const errMsg = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic prefixes its own messages with "Value error, " — not his language.
    const msgs = detail
      .map(d => (d?.msg || '').replace(/^Value error,\s*/i, ''))
      .filter(Boolean);
    return msgs.join('. ') || fallback;
  }
  if (typeof detail === 'string') return detail;
  return fallback;
};

// <input type="date"> speaks local dates. Slicing the ISO string gives the UTC date,
// which in US time zones is already tomorrow — so an untouched save used to walk the
// end date forward a day every time.
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const title = (t) => t[0].toUpperCase() + t.slice(1);

// "Silver and Gold", "Silver, Gold and Platinum"
const andList = (items) => {
  const names = items.map(title);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

function expiryLabel(iso) {
  if (!iso) return 'No end date';
  const d = new Date(iso);
  const past = d < new Date();
  return `${past ? 'Ended' : 'Ends'} ${d.toLocaleDateString()}`;
}

export default function AdminPromotionsTab() {
  return (
    <div className="space-y-8" data-testid="admin-promotions-tab">
      <CoachingCampaigns />
      <StreamPromotions />
    </div>
  );
}

// ── Coaching ───────────────────────────────────────────────────────────

function CoachingCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    title: '', discount_percent: 10, tiers: [...TIERS],
    basis: 'remaining', total_use_cap: '', expires_at: '',
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promotions/coaching`, { headers: headers() });
      setCampaigns(data || []);
    } catch {
      toast.error('Could not load coaching campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error('Give the campaign a name');
    if (!form.tiers.length) return toast.error('Pick at least one program tier');
    const percent = Number(form.discount_percent);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) {
      return toast.error('The discount must be between 1% and 99%');
    }
    try {
      await axios.post(`${API}/admin/promotions/coaching`, {
        title: form.title.trim(),
        discount_percent: percent,
        tiers: form.tiers,
        basis: form.basis,
        total_use_cap: form.total_use_cap ? Number(form.total_use_cap) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      }, { headers: headers() });
      toast.success('Campaign created — every practitioner has a code');
      setShowForm(false);
      setForm({ title: '', discount_percent: 10, tiers: [...TIERS], basis: 'remaining', total_use_cap: '', expires_at: '' });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not create the campaign'));
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/promotions/coaching/${c.id}`, { active: !c.active }, { headers: headers() });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not change this campaign'));
    }
  };

  const saveEdits = async (c, patch) => {
    try {
      await axios.patch(`${API}/admin/promotions/coaching/${c.id}`, patch, { headers: headers() });
      toast.success('Campaign updated');
      setExpanded(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update the campaign'));
    }
  };

  const renameCode = async (codeId, next) => {
    try {
      const { data } = await axios.patch(`${API}/admin/promotions/coaching/codes/${codeId}`,
        { code: next }, { headers: headers() });
      toast.success(`Code is now ${data.code}`);
      load();
      return true;
    } catch (err) {
      toast.error(errMsg(err, 'Could not rename that code'));
      return false;
    }
  };

  const setTiers = async (c, tiers) => {
    if (!tiers.length) return toast.error('A campaign needs at least one program');
    try {
      await axios.patch(`${API}/admin/promotions/coaching/${c.id}`, { tiers }, { headers: headers() });
      toast.success(`Now applies to ${andList(tiers)}`);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not change the programs'));
    }
  };

  const syncCodes = async (c) => {
    try {
      const { data } = await axios.post(`${API}/admin/promotions/coaching/${c.id}/sync-codes`, {}, { headers: headers() });
      toast.success(data.created ? `${data.created} new code(s) issued` : 'Everyone already has a code');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not issue codes'));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all of its codes?`)) return;
    try {
      await axios.delete(`${API}/admin/promotions/coaching/${c.id}`, { headers: headers() });
      toast.success('Campaign deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete this campaign'));
    }
  };

  return (
    <Card className="bg-neutral-900 border-neutral-700">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-neutral-100 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-amber-300" />
            Coaching Program Codes
          </CardTitle>
          <p className="text-sm text-neutral-400 mt-1">
            One campaign issues a code to every practitioner and they all draw down the same
            redemption pool, so you can see who brought each signup in. Paid-in-full programs only.
          </p>
        </div>
        <Button
          onClick={() => setShowForm(v => !v)}
          className="bg-amber-700 hover:bg-amber-600 text-white shrink-0"
          data-testid="new-coaching-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New campaign
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {showForm && (
          <div className="rounded-lg border border-neutral-700 bg-neutral-950 p-4 space-y-4" data-testid="coaching-campaign-form">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Campaign name</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Autumn 10% off"
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="coaching-title-input"
                />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Discount %</Label>
                <Input
                  type="number" min="1" max="99"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="coaching-percent-input"
                />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Total redemptions</Label>
                <Input
                  type="number" min="1"
                  value={form.total_use_cap}
                  onChange={(e) => setForm({ ...form, total_use_cap: e.target.value })}
                  placeholder="111 — leave blank for no limit"
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="coaching-cap-input"
                />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">End date</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60"
                  data-testid="coaching-expiry-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-wide">
                Programs this code works on
              </Label>
              <p className="text-xs text-neutral-500 mt-1">
                {form.tiers.length === TIERS.length
                  ? 'All three are included. Untick any program the code should not work on.'
                  : 'All three start included. Tick a program to let the code work on it.'}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {TIERS.map(tier => {
                  const on = form.tiers.includes(tier);
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        tiers: on ? form.tiers.filter(t => t !== tier) : [...form.tiers, tier],
                      })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm capitalize border transition-colors ${
                        on ? 'bg-amber-600/20 border-amber-500 text-amber-100'
                           : 'bg-neutral-900 border-neutral-600 text-neutral-300 hover:border-neutral-400'
                      }`}
                      data-testid={`coaching-tier-${tier}`}
                      aria-pressed={on}
                    >
                      <span
                        className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                          on ? 'bg-amber-500 border-amber-400' : 'border-neutral-500'
                        }`}
                      >
                        {on && <Check className="w-3 h-3 text-neutral-900" strokeWidth={3} />}
                      </span>
                      {tier}
                      <span
                        className={`text-[10px] uppercase tracking-wide ${
                          on ? 'text-amber-200/80' : 'text-rose-300'
                        }`}
                      >
                        {on ? 'included' : 'excluded'}
                      </span>
                    </button>
                  );
                })}
              </div>
              {form.tiers.length === 0 ? (
                <p
                  className="text-xs text-rose-300 mt-2 flex items-center gap-1.5"
                  data-testid="coaching-tier-warning"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  Pick at least one program.
                </p>
              ) : form.tiers.length < TIERS.length && (
                <p
                  className="text-xs text-amber-300 mt-2 flex items-center gap-1.5"
                  data-testid="coaching-tier-warning"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  The code will be refused on {andList(TIERS.filter(t => !form.tiers.includes(t)))}.
                </p>
              )}
            </div>

            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-wide">
                When the member already holds credit
              </Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="coaching-basis-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="remaining">Take the % off what they still owe</SelectItem>
                  <SelectItem value="full">Take the % off the full program price</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                On a $1,000 program with $300 already credited, a 10% code is worth $70 the first
                way and $100 the second.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="text-neutral-400">
                Cancel
              </Button>
              <Button onClick={create} className="bg-amber-700 hover:bg-amber-600" data-testid="coaching-save-btn">
                Create campaign
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-neutral-500 text-sm py-6 text-center">Loading…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-neutral-500 text-sm py-6 text-center italic" data-testid="coaching-empty">
            No coaching campaigns yet.
          </p>
        ) : campaigns.map(c => {
          const used = c.uses_count || 0;
          const cap = c.total_use_cap;
          return (
            <div
              key={c.id}
              className="rounded-lg border border-neutral-700 bg-neutral-950 p-4"
              data-testid={`coaching-campaign-${c.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-neutral-100 font-semibold">{c.title}</h4>
                    <Badge className="bg-amber-900/40 text-amber-100 border-amber-700">
                      {c.discount_percent}% off
                    </Badge>
                    {!c.active && <Badge className="bg-neutral-800 text-neutral-300 border-neutral-600">Paused</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400 mt-2">
                    <span data-testid={`coaching-tiers-${c.id}`}>
                      {(c.tiers || []).map(title).join(', ')}
                      {(c.tiers || []).length < TIERS.length && (
                        <span className="text-amber-300">
                          {' '}· not {andList(TIERS.filter(t => !(c.tiers || []).includes(t)))}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />{expiryLabel(c.expires_at)}</span>
                    <span className="flex items-center gap-1" data-testid={`coaching-pool-${c.id}`}>
                      <BarChart3 className="w-3 h-3" />
                      {cap ? `${used} of ${cap} redeemed` : `${used} redeemed`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />{(c.codes || []).length} codes
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={!!c.active}
                    onCheckedChange={() => toggleActive(c)}
                    data-testid={`coaching-toggle-${c.id}`}
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={() => syncCodes(c)}
                    className="border-neutral-600 text-neutral-200 hover:bg-neutral-800"
                    data-testid={`coaching-sync-${c.id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Issue missing codes
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => remove(c)}
                    className="border-rose-800 text-rose-200 hover:bg-rose-950"
                    data-testid={`coaching-delete-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === `${c.id}:edit` ? null : `${c.id}:edit`)}
                  className="text-xs text-amber-300 underline"
                  data-testid={`coaching-edit-${c.id}`}
                >
                  {expanded === `${c.id}:edit` ? 'Close' : 'Edit campaign'}
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === `${c.id}:tiers` ? null : `${c.id}:tiers`)}
                  className="text-xs text-amber-300 underline"
                  data-testid={`coaching-edit-tiers-${c.id}`}
                >
                  {expanded === `${c.id}:tiers` ? 'Done' : 'Change programs'}
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === `${c.id}:codes` ? null : `${c.id}:codes`)}
                  className="text-xs text-amber-300 underline"
                  data-testid={`coaching-codes-toggle-${c.id}`}
                >
                  {expanded === `${c.id}:codes` ? 'Hide codes' : 'Show codes by practitioner'}
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === `${c.id}:perf` ? null : `${c.id}:perf`)}
                  className="text-xs text-amber-300 underline"
                  data-testid={`coaching-performance-toggle-${c.id}`}
                >
                  {expanded === `${c.id}:perf` ? 'Hide performance' : 'Performance'}
                </button>
              </div>

              {expanded === `${c.id}:tiers` && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`coaching-tier-editor-${c.id}`}>
                  {TIERS.map(tier => {
                    const on = (c.tiers || []).includes(tier);
                    return (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => setTiers(c, on ? (c.tiers || []).filter(t => t !== tier)
                                                     : [...(c.tiers || []), tier])}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm capitalize border transition-colors ${
                          on ? 'bg-amber-600/20 border-amber-500 text-amber-100'
                             : 'bg-neutral-900 border-neutral-600 text-neutral-300 hover:border-neutral-400'
                        }`}
                        data-testid={`coaching-tier-edit-${c.id}-${tier}`}
                      >
                        <span
                          className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                            on ? 'bg-amber-500 border-amber-400' : 'border-neutral-500'
                          }`}
                        >
                          {on && <Check className="w-3 h-3 text-neutral-900" strokeWidth={3} />}
                        </span>
                        {tier}
                        <span
                          className={`text-[10px] uppercase tracking-wide ${
                            on ? 'text-amber-200/80' : 'text-rose-300'
                          }`}
                        >
                          {on ? 'included' : 'excluded'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {expanded === `${c.id}:edit` && (
                <CampaignEditor campaign={c} onSave={(patch) => saveEdits(c, patch)} />
              )}

              {expanded === `${c.id}:perf` && <CampaignPerformance campaignId={c.id} />}

              {expanded === `${c.id}:codes` && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`coaching-codes-table-${c.id}`}>
                    <thead>
                      <tr className="text-neutral-400 text-xs uppercase tracking-wide">
                        <th className="text-left py-2 font-medium">Practitioner</th>
                        <th className="text-left py-2 font-medium">Code</th>
                        <th className="text-right py-2 font-medium">Redeemed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(c.codes || []).map(code => {
                        // Two practitioners can share a name, so show the email to tell them apart.
                        const sharesName = (c.codes || []).filter(
                          other => other.practitioner_name === code.practitioner_name
                        ).length > 1;
                        return (
                        <tr key={code.id} className="border-t border-neutral-800">
                          <td className="py-2 text-neutral-200">
                            {code.practitioner_name || '—'}
                            {sharesName && code.practitioner_email && (
                              <span className="block text-xs text-neutral-400">
                                {code.practitioner_email}
                              </span>
                            )}
                          </td>
                          <td className="py-2">
                            <CodeCell code={code} onRename={renameCode} />
                          </td>
                          <td className="py-2 text-right text-neutral-300">{code.uses_count || 0}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── Editing a live campaign ────────────────────────────────────────────

function CampaignEditor({ campaign, onSave }) {
  const initialDate = toDateInput(campaign.expires_at);
  const [form, setForm] = useState({
    title: campaign.title || '',
    discount_percent: campaign.discount_percent ?? 10,
    total_use_cap: campaign.total_use_cap ?? '',
    expires_at: initialDate,
    basis: campaign.basis || 'remaining',
  });

  const submit = () => {
    if (!form.title.trim()) return toast.error('Give the campaign a name');
    const percent = Number(form.discount_percent);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) {
      return toast.error('The discount must be between 1% and 99%');
    }
    const patch = {
      title: form.title.trim(),
      discount_percent: percent,
      total_use_cap: form.total_use_cap === '' ? null : Number(form.total_use_cap),
      basis: form.basis,
    };
    // Only touch the end date when he actually changed it, so saving a name never
    // moves the date.
    if (form.expires_at !== initialDate) {
      patch.expires_at = form.expires_at
        ? new Date(`${form.expires_at}T23:59:59`).toISOString()
        : null;
    }
    onSave(patch);
  };

  return (
    <div
      className="mt-3 rounded-lg border border-neutral-700 bg-neutral-900 p-4 space-y-4"
      data-testid={`coaching-editor-${campaign.id}`}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-wide">Campaign name</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-neutral-950 border-neutral-700 text-neutral-100 mt-1"
            data-testid={`coaching-edit-title-${campaign.id}`}
          />
        </div>
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-wide">Discount %</Label>
          <Input
            type="number" min="1" max="99"
            value={form.discount_percent}
            onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
            className="bg-neutral-950 border-neutral-700 text-neutral-100 mt-1"
            data-testid={`coaching-edit-percent-${campaign.id}`}
          />
        </div>
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-wide">Total redemptions</Label>
          <Input
            type="number" min="1"
            value={form.total_use_cap}
            onChange={(e) => setForm({ ...form, total_use_cap: e.target.value })}
            placeholder="Leave blank for no limit"
            className="bg-neutral-950 border-neutral-700 text-neutral-100 mt-1"
            data-testid={`coaching-edit-cap-${campaign.id}`}
          />
          <p className="text-xs text-neutral-500 mt-1">
            Shared by every practitioner's code — {campaign.uses_count || 0} used so far.
          </p>
        </div>
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-wide">End date</Label>
          <Input
            type="date"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            className="bg-neutral-950 border-neutral-700 text-neutral-100 mt-1 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60"
            data-testid={`coaching-edit-expiry-${campaign.id}`}
          />
          <p className="text-xs text-neutral-500 mt-1">Clear the date to run with no end.</p>
        </div>
      </div>

      <div>
        <Label className="text-neutral-300 text-xs uppercase tracking-wide">
          When the member already holds credit
        </Label>
        <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
          <SelectTrigger
            className="bg-neutral-950 border-neutral-700 text-neutral-100 mt-1"
            data-testid={`coaching-edit-basis-${campaign.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="remaining">Take the % off what they still owe</SelectItem>
            <SelectItem value="full">Take the % off the full program price</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-neutral-500">
        Changing the percentage does not rewrite codes already issued — {campaign.title} keeps its
        existing code strings, and they simply discount by the new amount.
      </p>

      <div className="flex justify-end">
        <Button
          onClick={submit}
          className="bg-amber-700 hover:bg-amber-600"
          data-testid={`coaching-edit-save-${campaign.id}`}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

// ── Renaming a practitioner's code ─────────────────────────────────────

function CodeCell({ code, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(code.code);

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="font-mono text-amber-200 bg-amber-950/40 px-2 py-0.5 rounded">
          {code.code}
        </span>
        <button
          type="button"
          onClick={() => { setValue(code.code); setEditing(true); }}
          className="text-neutral-500 hover:text-amber-300"
          title="Rename this code"
          data-testid={`coaching-code-rename-${code.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  const commit = async () => {
    const next = value.trim().toUpperCase();
    if (next === code.code) return setEditing(false);
    if (await onRename(code.id, next)) setEditing(false);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        maxLength={24}
        className="font-mono text-sm w-48 px-2 py-0.5 rounded bg-neutral-950 border border-amber-600 text-amber-100"
        data-testid={`coaching-code-input-${code.id}`}
      />
      <button
        type="button"
        onClick={commit}
        className="text-emerald-300 hover:text-emerald-200"
        data-testid={`coaching-code-save-${code.id}`}
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="text-neutral-500 hover:text-neutral-300"
        data-testid={`coaching-code-cancel-${code.id}`}
      >
        <X className="w-4 h-4" />
      </button>
    </span>
  );
}

// ── Campaign performance ───────────────────────────────────────────────
// He asked for the per-practitioner codes so redemptions could be TRACKED per
// practitioner, so the tracking ships with them rather than as a follow-up.

function CampaignPerformance({ campaignId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/admin/promotions/coaching/${campaignId}/performance`,
          { headers: headers() },
        );
        if (!dead) setData(res.data);
      } catch (err) {
        if (!dead) setError(errMsg(err, 'Could not load performance'));
      }
    })();
    return () => { dead = true; };
  }, [campaignId]);

  if (error) return <p className="text-sm text-rose-300 mt-3">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-500 mt-3">Loading performance…</p>;

  const { totals, by_practitioner: rows, redemptions } = data;
  const converting = rows.filter(r => r.redemptions > 0);

  return (
    <div className="mt-4 space-y-4" data-testid={`coaching-performance-${campaignId}`}>
      <div className="grid gap-3 sm:grid-cols-4">
        {[
          ['Redeemed', totals.redemptions, totals.pool_cap ? `of ${totals.pool_cap}` : 'no limit'],
          ['Revenue', `$${totals.revenue.toFixed(2)}`, 'collected on these signups'],
          ['Discount given', `$${totals.discount_given.toFixed(2)}`, 'absorbed by the platform'],
          ['Practitioners converting', `${totals.practitioners_converting}`, `of ${totals.codes_issued} with a code`],
        ].map(([label, value, note]) => (
          <div key={label} className="rounded-lg border border-neutral-800 bg-neutral-900 p-3">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</p>
            <p className="text-xl font-semibold text-neutral-100 mt-0.5">{value}</p>
            <p className="text-[11px] text-neutral-500 mt-0.5">{note}</p>
          </div>
        ))}
      </div>

      {converting.length === 0 ? (
        <p className="text-sm text-neutral-500 italic py-3" data-testid={`coaching-performance-empty-${campaignId}`}>
          Nobody has redeemed a code on this campaign yet. Once they do, you will see which
          practitioner brought each signup in.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`coaching-performance-table-${campaignId}`}>
            <thead>
              <tr className="text-neutral-400 text-xs uppercase tracking-wide">
                <th className="text-left py-2 font-medium">Practitioner</th>
                <th className="text-left py-2 font-medium">Code</th>
                <th className="text-right py-2 font-medium">Signups</th>
                <th className="text-right py-2 font-medium">Revenue</th>
                <th className="text-right py-2 font-medium">Discount given</th>
              </tr>
            </thead>
            <tbody>
              {converting.map(r => (
                <tr key={r.code_id} className="border-t border-neutral-800">
                  <td className="py-2 text-neutral-200">
                    {r.practitioner_name || '—'}
                    {converting.filter(o => o.practitioner_name === r.practitioner_name).length > 1
                      && r.practitioner_email && (
                      <span className="block text-xs text-neutral-400">{r.practitioner_email}</span>
                    )}
                  </td>
                  <td className="py-2">
                    <span className="font-mono text-amber-200 bg-amber-950/40 px-2 py-0.5 rounded">
                      {r.code}
                    </span>
                  </td>
                  <td className="py-2 text-right text-neutral-100 font-semibold">{r.redemptions}</td>
                  <td className="py-2 text-right text-emerald-300">${r.revenue.toFixed(2)}</td>
                  <td className="py-2 text-right text-neutral-400">${r.discount_given.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {redemptions.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Every redemption</p>
          <div className="space-y-1.5" data-testid={`coaching-redemption-log-${campaignId}`}>
            {redemptions.map(r => (
              <div
                key={r.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm border-b border-neutral-800 pb-1.5"
              >
                <span className="text-neutral-200">
                  {r.member_name || r.member_email || 'Member'}
                  <span className="text-neutral-500"> — via {r.practitioner_name || r.code}</span>
                </span>
                <span className="text-neutral-400 text-xs">
                  ${r.revenue.toFixed(2)} paid · ${r.discount_given.toFixed(2)} off ·{' '}
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Livestreams ────────────────────────────────────────────────────────

function StreamPromotions() {
  const [promos, setPromos] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', kind: 'sale', code: '', discount_percent: 20,
    stream_id: '', purchase_types: ['live', 'replay'], total_use_cap: '', expires_at: '',
  });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/admin/promotions/streams`, { headers: headers() }),
        axios.get(`${API}/live-streams?per_page=50`, { headers: headers() }),
      ]);
      setPromos(p.data || []);
      // /live-streams is paginated; only paid streams can carry a discount.
      const list = Array.isArray(s.data) ? s.data : (s.data?.streams || []);
      const seen = new Set();
      setStreams(list.filter(st => {
        if (!st.is_paid || seen.has(st.id)) return false;
        seen.add(st.id);
        return true;
      }));
    } catch {
      toast.error('Could not load livestream promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const streamTitle = (id) => streams.find(s => s.id === id)?.title || 'Stream';

  const create = async () => {
    if (!form.title.trim()) return toast.error('Give the promotion a name');
    if (!form.stream_id) return toast.error('Choose a stream');
    if (form.kind === 'code' && !form.code.trim()) return toast.error('Enter the code');
    const percent = Number(form.discount_percent);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) {
      return toast.error('The discount must be between 1% and 99%');
    }
    try {
      await axios.post(`${API}/admin/promotions/streams`, {
        title: form.title.trim(),
        kind: form.kind,
        code: form.kind === 'code' ? form.code.trim().toUpperCase() : null,
        discount_percent: percent,
        stream_id: form.stream_id,
        purchase_types: form.purchase_types,
        total_use_cap: form.total_use_cap ? Number(form.total_use_cap) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      }, { headers: headers() });
      toast.success('Promotion created');
      setShowForm(false);
      setForm({ title: '', kind: 'sale', code: '', discount_percent: 20, stream_id: '', purchase_types: ['live', 'replay'], total_use_cap: '', expires_at: '' });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not create the promotion'));
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.patch(`${API}/admin/promotions/streams/${p.id}`, { active: !p.active }, { headers: headers() });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not change this promotion'));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promotions/streams/${p.id}`, { headers: headers() });
      toast.success('Promotion deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete this promotion'));
    }
  };

  const togglePurchaseType = (t) => setForm({
    ...form,
    purchase_types: form.purchase_types.includes(t)
      ? form.purchase_types.filter(x => x !== t)
      : [...form.purchase_types, t],
  });

  return (
    <Card className="bg-neutral-900 border-neutral-700">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-neutral-100 flex items-center gap-2">
            <Tv className="w-5 h-5 text-sky-300" />
            Livestream Promotions
          </CardTitle>
          <p className="text-sm text-neutral-400 mt-1">
            A sale price shows the reduction with no code to type. A code is the quieter option for
            a pre-sale push. Either can cover the live seat, the archived replay, or both.
          </p>
        </div>
        <Button
          onClick={() => setShowForm(v => !v)}
          className="bg-sky-800 hover:bg-sky-700 text-white shrink-0"
          data-testid="new-stream-promo-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New promotion
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {showForm && (
          <div className="rounded-lg border border-neutral-700 bg-neutral-950 p-4 space-y-4" data-testid="stream-promo-form">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Promotion name</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Early bird"
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="stream-promo-title-input"
                />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Stream</Label>
                <Select value={form.stream_id} onValueChange={(v) => setForm({ ...form, stream_id: v })}>
                  <SelectTrigger
                    className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                    data-testid="stream-promo-stream-select"
                  >
                    <SelectValue placeholder="Choose a stream" />
                  </SelectTrigger>
                  <SelectContent>
                    {streams.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">How it applies</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger
                    className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                    data-testid="stream-promo-kind-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Sale price, no code needed</SelectItem>
                    <SelectItem value="code">Code entered at checkout</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Discount %</Label>
                <Input
                  type="number" min="1" max="99"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="stream-promo-percent-input"
                />
              </div>
              {form.kind === 'code' && (
                <div>
                  <Label className="text-neutral-300 text-xs uppercase tracking-wide">Code</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="PRESALE20"
                    className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1 font-mono"
                    data-testid="stream-promo-code-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">End date</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60"
                  data-testid="stream-promo-expiry-input"
                />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-wide">Total redemptions</Label>
                <Input
                  type="number" min="1"
                  value={form.total_use_cap}
                  onChange={(e) => setForm({ ...form, total_use_cap: e.target.value })}
                  placeholder="Leave blank for no limit"
                  className="bg-neutral-900 border-neutral-700 text-neutral-100 mt-1"
                  data-testid="stream-promo-cap-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-wide">Covers</Label>
              <div className="flex gap-2 mt-2">
                {[['live', 'Live seat'], ['replay', 'Archived replay']].map(([val, lbl]) => {
                  const on = form.purchase_types.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => togglePurchaseType(val)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        on ? 'bg-sky-600 border-sky-300 text-white font-semibold ring-2 ring-sky-400/40'
                           : 'bg-neutral-900 border-neutral-700 text-neutral-500 hover:text-neutral-200'
                      }`}
                      data-testid={`stream-promo-type-${val}`}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="text-neutral-400">
                Cancel
              </Button>
              <Button onClick={create} className="bg-sky-800 hover:bg-sky-700" data-testid="stream-promo-save-btn">
                Create promotion
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-neutral-500 text-sm py-6 text-center">Loading…</p>
        ) : promos.length === 0 ? (
          <p className="text-neutral-500 text-sm py-6 text-center italic" data-testid="stream-promos-empty">
            No livestream promotions yet.
          </p>
        ) : promos.map(p => (
          <div
            key={p.id}
            className="rounded-lg border border-neutral-700 bg-neutral-950 p-4 flex flex-wrap items-start justify-between gap-3"
            data-testid={`stream-promo-${p.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-neutral-100 font-semibold">{p.title}</h4>
                <Badge className="bg-sky-900/40 text-sky-100 border-sky-700">
                  {p.discount_percent}% off
                </Badge>
                {p.kind === 'code' ? (
                  <span className="font-mono text-sky-200 bg-sky-950/50 px-2 py-0.5 rounded text-xs">
                    {p.code}
                  </span>
                ) : (
                  <Badge className="bg-emerald-900/40 text-emerald-100 border-emerald-700">Sale price</Badge>
                )}
                {!p.active && <Badge className="bg-neutral-800 text-neutral-300 border-neutral-600">Paused</Badge>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400 mt-2">
                <span>{streamTitle(p.stream_id || (p.target_ids || [])[0])}</span>
                <span>{(p.purchase_types || []).map(t => t === 'live' ? 'Live' : 'Replay').join(' + ')}</span>
                <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />{expiryLabel(p.expires_at)}</span>
                <span>
                  {p.total_use_cap ? `${p.uses_count || 0} of ${p.total_use_cap} redeemed`
                                   : `${p.uses_count || 0} redeemed`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch
                checked={!!p.active}
                onCheckedChange={() => toggleActive(p)}
                data-testid={`stream-promo-toggle-${p.id}`}
              />
              <Button
                variant="outline" size="sm"
                onClick={() => remove(p)}
                className="border-rose-800 text-rose-200 hover:bg-rose-950"
                data-testid={`stream-promo-delete-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
